/**
 * PlanBanner.js — the 80% warning and the read-only banner (spec §1.3.3).
 *
 * Reads GET /api/plan. With PLANS_ENFORCED off it has nothing to say at all —
 * nothing is refused, so no banner may claim otherwise — and once the switch is
 * on it speaks only when an account is actually near or over a limit.
 *
 * There is no checkout yet, so the action is a mailto carrying the product,
 * account and plan (§1.3.2).
 */
import React, { useState } from 'react';
import { usePlan } from '../lib/queries';
import { planBanner, contactHref, trialDaysLeft } from '../lib/planCopy';
import Icon from './Icon';

export default function PlanBanner() {
  const { data: plan } = usePlan();
  const [dismissed, setDismissed] = useState(false);

  const banner = planBanner(plan);
  if (!banner) return null;
  // A warning can be put away for the session; a block cannot, because it
  // explains why the app is refusing work.
  if (dismissed && banner.tone === 'warning') return null;

  const days = trialDaysLeft(plan?.trial_ends_at);
  const isError = banner.tone === 'error';

  return (
    <div
      className={`inline-msg ${isError ? 'is-error' : 'is-warning'}`}
      role={isError ? 'alert' : 'status'}
      style={{ marginBottom: 16 }}
    >
      <Icon name={isError ? 'alert' : 'warning'} />
      <div style={{ flex: 1 }}>
        <strong>{banner.title}</strong>
        <div>{banner.body}</div>
        {plan?.status === 'trial' && days != null && (
          <div className="meta" style={{ marginTop: 4 }}>
            {days === 0 ? 'Your trial ends today.' : `${days} day${days === 1 ? '' : 's'} left on your trial.`}
          </div>
        )}
      </div>
      <a
        className="btn btn-secondary btn-sm"
        href={contactHref({ plan: plan?.plan, tenantId: plan?.tenant_id, reason: banner.reason })}
      >
        {banner.action}
      </a>
      {banner.tone === 'warning' && (
        <button type="button" className="icon-btn" aria-label="Dismiss" onClick={() => setDismissed(true)}>
          <Icon name="close" />
        </button>
      )}
    </div>
  );
}
